/* eslint-disable no-param-reassign */
import { ApolloCache, useMutation, useQuery } from '@apollo/client';
import { Store } from '@website/Store';
import useIsAdmin from '@website/hooks/useIsAdmin';
import { preventURLsValidator } from '@website/libs/validator/preventURLsValidator';
import {
  ChatConversationStatus,
  ChatMessageInput,
  CloseChatConversationDocument,
  CreateChatMessageDocument,
  CreateChatMessageMutation,
  InboxConversationDocument,
  MarkChatConversationReadDocument,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useRef } from 'react';
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  Container,
  Dropdown,
  Form,
  Row,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { BsFillCaretLeftFill } from 'react-icons/bs';
import { SubmitButton } from '../forms/fields/SubmitButton';
import ErrorPage from '../utilities/ErrorPage';
import LoadingPage from '../utilities/LoadingPage';
import OfflineAlert from '../utilities/OfflineAlert';
import Truncate from '../utilities/Truncate';
import Message from './Message';
import LeaveGroupConversation from './utils/LeaveGroupConversation';
import CannedResponseOptions from './utils/cannedResponses/CannedResponseOptions';
import getParticipantsListExcludingSelf from './utils/getParticipantsExcludingSelf';

/**
 * Create the optimistic response
 *
 * This will show and then be replaced as soon as we get a network response. Note `update` from useMutation runs twice -
 * once with this and then once again as soon as we get a correct response.
 */
export function createOptimisticResponse(
  input: ChatMessageInput,
): CreateChatMessageMutation {
  const created = DateTime.now().toUTC().toISO();
  const isHost = input.participantID.startsWith('host');

  return {
    createChatMessage: {
      // Note we use the ID to detect it is optimistic in the {@link Message} component
      id: `msg_tmp_id_${created}`,
      text: input.text,
      created,
      hostTeamMemberUserID: null,
      user: isHost
        ? null
        : {
            id: input.participantID,
            ...({ __typename: 'UserPublicProfile' } as any),
          },
      host: isHost
        ? {
            id: input.participantID,
            ...({ __typename: 'Host' } as any),
          }
        : null,
      system: null,
      ...({ __typename: 'ChatMessage' } as any),
    },
  };
}

/**
 * Update the conversation cache
 */
export function updateConversationCache(
  cache: ApolloCache<any>,
  { data }: { data: CreateChatMessageMutation },
  conversationID: string,
) {
  const conversationCacheID = cache.identify({
    __typename: 'ChatConversation',
    id: conversationID,
  });

  const messageCacheID = cache.identify({
    __typename: 'ChatMessage',
    id: data.createChatMessage.id,
  });

  // Insert the message into the conversation cache
  cache.modify({
    id: conversationCacheID,
    fields: {
      messages(cachedMessages) {
        return {
          items: [...cachedMessages.items, { __ref: messageCacheID }],
        };
      },
    },
  });
}

/**
 * Reply box
 *
 * Text box for sending messages
 */
export function ReplyBox({
  userID,
  conversationID,
}: {
  userID: string;
  conversationID: string;
}) {
  const { push, query } = useRouter();

  const [createMessage] = useMutation(CreateChatMessageDocument, {
    // Update the cache to add the new message in immediately
    update: (cache, { data }) =>
      updateConversationCache(cache, { data }, conversationID),
  });

  const isHost = userID.startsWith('host');

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
    setValue,
    getValues,
    reset,
    watch,
  } = useForm<ChatMessageInput>({
    mode: 'onChange',
    defaultValues: {
      conversationID,
      participantID: userID,
      text: '',
      conversationStatus: isHost
        ? ChatConversationStatus.WAITING_FOR_CUSTOMER_REPLY
        : ChatConversationStatus.OPEN,
    },
  });

  const textAreaContainerRef = useRef<HTMLDivElement>(null);
  const textAreaRef = textAreaContainerRef?.current
    ?.firstChild as HTMLTextAreaElement;

  /**
   * Auto-expand the text area as you type
   */
  useEffect(() => {
    textAreaRef?.addEventListener(
      'input',
      function autoResize(event: InputEvent) {
        const offset = textAreaRef.offsetHeight - textAreaRef.clientHeight;
        (event.target as any).style.height = 'auto';
        (event.target as any).style.height = `${
          (event.target as any).scrollHeight + offset
        }px`;
      },
    );
  }, [textAreaRef]);

  const textMessage = watch('text');

  const sendMessage = async (input: ChatMessageInput) => {
    try {
      await createMessage({
        variables: { input },
        optimisticResponse: createOptimisticResponse(input),
      });

      textAreaRef.style.height = 'auto';
      reset();
    } catch (e) {
      if (e.networkError) {
        alert(
          'Internet connection lost - please retry when you are connected.',
        );
      } else {
        alert(e);
      }
    }
  };

  const [closeConversation] = useMutation(CloseChatConversationDocument, {
    update(cache, { data }) {
      // evict from cache after closing conversation, this is for hosts only and
      // hosts will be directed to the inbox
      if (getValues('conversationStatus') === ChatConversationStatus.CLOSED) {
        const conversationCacheID = cache.identify({
          __typename: 'ChatConversation',
          id: data.changeChatConversationStatus.id,
        });
        cache.evict({ id: conversationCacheID });
      }
    },
  });

  // If the user is a host, show a button to close the conversation
  const markConversationAsClosed = async () => {
    try {
      setValue('conversationStatus', ChatConversationStatus.CLOSED);
      await closeConversation({
        variables: {
          input: {
            conversationID,
            status: ChatConversationStatus.CLOSED,
          },
        },
      });

      // Redirect to open inbox
      push(`/hosts/dashboard/${query.hostURL!}/messages`);
    } catch (e) {
      if (e.networkError) {
        alert(
          'Internet connection lost - please retry when you are connected.',
        );
      } else {
        alert(e);
      }
    }
  };

  const sendMessageWithStatus = async (status: ChatConversationStatus) => {
    setValue('conversationStatus', status);
    // do not submit form if the text is not empty, but send status
    if (getValues('text') !== '') {
      // submit the form
      await handleSubmit(sendMessage)();
    }

    // for cache purposes, so it can be evicted.
    if (status === ChatConversationStatus.CLOSED) {
      await markConversationAsClosed();
    }

    // Redirect to open inbox
    if (status === ChatConversationStatus.WAITING_FOR_CUSTOMER_REPLY) {
      push(`/hosts/dashboard/${query.hostURL!}/messages`);
    }
  };

  return (
    <Container className="py-1 py-md-3 py-lg-4">
      {isHost && (
        <CannedResponseOptions
          setValue={setValue}
          textAreaRef={textAreaRef}
          getValues={getValues}
        />
      )}

      <Form onSubmit={handleSubmit(sendMessage)}>
        <Row className="g-0">
          <Col>
            <Form.Group
              controlId="text"
              className="mb-0"
              ref={textAreaContainerRef}
            >
              <Form.Control
                aria-label="Your message"
                as="textarea"
                isInvalid={!!errors.text}
                rows={2}
                {...register('text', {
                  required: true,
                  validate: preventURLsValidator,
                })}
                style={{ resize: 'none', maxHeight: '40vh' }}
                required
                autoFocus
              />

              {errors.text && errors.text.type === 'validate' && (
                <Form.Control.Feedback type="invalid">
                  You're not allowed to include links or emails in your message.
                </Form.Control.Feedback>
              )}

              {errors.text && errors.text.type === 'required' && (
                <Form.Control.Feedback type="invalid">
                  Please enter the message you would like to send.
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col xs="auto">
            {/* show simple button to giest */}
            {!isHost && (
              <SubmitButton
                text="Send"
                divClassName="h-100"
                className="h-100 ms-1 ms-lg-3 px-2"
                isSubmitting={isSubmitting}
              />
            )}
            {/* show button group for hosts */}
            {isHost && (
              <Dropdown className="h-100 ms-1 ms-lg-3" as={ButtonGroup}>
                <Button
                  type="submit"
                  variant="primary"
                  style={{ letterSpacing: 0 }}
                  className="h-100"
                >
                  Send
                </Button>
                <Dropdown.Toggle
                  split
                  variant="primary"
                  id="dropdown-split-basic"
                />
                <Dropdown.Menu>
                  {textMessage && (
                    <>
                      <Dropdown.Item
                        onClick={() =>
                          sendMessageWithStatus(ChatConversationStatus.OPEN)
                        }
                        as="button"
                        type="button"
                        eventKey={1}
                      >
                        Send
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() =>
                          sendMessageWithStatus(
                            ChatConversationStatus.WAITING_FOR_CUSTOMER_REPLY,
                          )
                        }
                        as="button"
                        type="button"
                        eventKey={2}
                      >
                        Send (set as waiting on customer)
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={() =>
                          sendMessageWithStatus(ChatConversationStatus.CLOSED)
                        }
                        as="button"
                        type="button"
                        eventKey={3}
                      >
                        Send (set as resolved)
                      </Dropdown.Item>
                    </>
                  )}
                  {!textMessage && (
                    <Dropdown.Item
                      onClick={() => markConversationAsClosed()}
                      as="button"
                      type="button"
                      eventKey={1}
                    >
                      Set as resolved
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </Col>
        </Row>
      </Form>
    </Container>
  );
}

/**
 * Conversation View
 *
 * Includes messages, as well as a text box for sending new messages.
 */
export default function Conversation({
  userID,
  conversationID,
}: {
  userID: string;
  conversationID: string;
}) {
  const { asPath, query } = useRouter();
  const messagesContainerRef = useRef<HTMLDivElement>();

  // Get the data
  const { data, loading, error } = useQuery(InboxConversationDocument, {
    variables: { conversationID },
    pollInterval: 5000,
  });

  const conversation = data?.chatConversation;
  const isAdmin = useIsAdmin();
  const { state } = useContext(Store);

  const isHost = userID.startsWith('host');

  // Sort messages in date order
  const sortedMessages = useMemo(
    () =>
      conversation?.messages.items.slice().sort((a, b) => {
        return new Date(a.created).getTime() - new Date(b.created).getTime();
      }),
    [conversation?.messages.items],
  );

  // Setup the mark read mutation
  const [markRead] = useMutation(MarkChatConversationReadDocument, {
    onError: () => {}, // Handle errors silently
  });

  // On new messages
  useEffect(() => {
    if (sortedMessages) {
      // Scroll the latest message into view
      messagesContainerRef?.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'end',
      });

      // mark as read for admin only in their own conversation not for the host
      // or guests
      if (isAdmin && userID === state.user.id) {
        markRead({
          variables: {
            input: {
              conversationID,
              participantID: userID,
            },
          },
        });
      } else {
        // Update the last read time
        markRead({
          variables: {
            input: {
              conversationID,
              participantID: userID,
            },
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedMessages?.length]);

  if (loading && !data) return <LoadingPage />;
  // Note if this throws after we have data (e.g. on polling) we shouldn't show any errors
  if (!data && error) return <ErrorPage error={error} />;

  return (
    <>
      {isHost && (
        <Breadcrumb className="py-3">
          <Link
            href={`/hosts/dashboard/${query.hostURL}`}
            passHref
            legacyBehavior
          >
            <Breadcrumb.Item href="">Host Dashboard</Breadcrumb.Item>
          </Link>
          <Link
            href={`/hosts/dashboard/${query.hostURL}/messages`}
            passHref
            legacyBehavior
          >
            <Breadcrumb.Item>Inbox</Breadcrumb.Item>
          </Link>
          <Breadcrumb.Item active>
            {conversation.title ||
              getParticipantsListExcludingSelf(
                conversation.participants.items,
                userID,
              )}
          </Breadcrumb.Item>
        </Breadcrumb>
      )}

      <div className="py-2 py-md-3 border-bottom shadow">
        <Container>
          <div className="d-flex align-items-top">
            <div>
              <Link
                href={asPath.replace(query.conversationID as string, '')}
                legacyBehavior
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-100 me-2"
                  style={{ letterSpacing: 0 }}
                >
                  <BsFillCaretLeftFill />
                  Inbox
                </Button>
              </Link>
            </div>
            <div className="flex-grow-1">
              <h1 className="h5 my-0">
                {conversation.title ||
                  getParticipantsListExcludingSelf(
                    conversation.participants.items,
                    userID,
                  )}
              </h1>
              <Truncate lines={2} className="text-muted my-0 small">
                {conversation.title &&
                  getParticipantsListExcludingSelf(
                    conversation.participants.items,
                    userID,
                  )}
              </Truncate>
            </div>
            <div className="d-flex align-items-center">
              <LeaveGroupConversation
                conversationId={conversationID}
                userID={userID}
              />
            </div>
          </div>
        </Container>
      </div>

      <div
        style={{ flex: 1, overflow: 'auto' }}
        className="d-flex flex-column-reverse pt-3"
      >
        <Container ref={messagesContainerRef} className="pt-2">
          <Row>
            {sortedMessages.map((message) => (
              <Message
                message={message}
                key={message.id}
                inboxUserID={userID}
              />
            ))}
          </Row>

          <OfflineAlert
            variant="light"
            message="If you send a message, we'll try to post it when you have a WiFi/3G connection again."
            className="small"
          />
        </Container>
      </div>

      <div className="border-top">
        <ReplyBox userID={userID} conversationID={conversation.id} />
      </div>
    </>
  );
}
